import { FormGroup } from '@angular/forms';
import { Language, LanguageService } from '../../../services/language/language.service';
import { ThemeService } from '../../../services/theme/theme.service';
import { LocalizedCountry, UtilityService } from '../../../services/utility.service';
import { WizardTimezone } from '../auth-wizard-timezone.catalog';

export interface WizardPreferenceState {
  language: Language;
  countries: LocalizedCountry[];
}

export class WizardPreferenceCoordinator {
  constructor(
    private readonly themeService: ThemeService,
    private readonly languageService: LanguageService,
    private readonly utilityService: UtilityService
  ) {}

  nextStep(current: number, stepCount: number, form: FormGroup): number {
    if (current === 2 && !this.hasValidProfile(form)) {
      this.markFormGroupTouched(form);
      return current;
    }
    return current < stepCount - 1 ? current + 1 : current;
  }

  previousStep(current: number): number {
    return current > 0 ? current - 1 : current;
  }

  setTheme(form: FormGroup, theme: 'light' | 'dark'): void {
    this.themeService.setTheme(theme);
    form.patchValue({ theme });
  }

  setLanguage(form: FormGroup, language: Language): WizardPreferenceState {
    this.languageService.setLanguage(language);
    form.patchValue({ language });
    return { language, countries: this.refreshCountries(form, language) };
  }

  localizeCountry(form: FormGroup, value: string, language: Language): void {
    const localized = this.getLocalizedCountryName(value, language);
    if (localized && localized !== value) form.patchValue({ country: localized });
  }

  organizationLabel(
    form: FormGroup | undefined,
    control: string,
    options: { value: string; label: string }[],
    fallback: string
  ): string {
    const value = form?.get(control)?.value;
    return options.find((option) => option.value === value)?.label || fallback;
  }

  markFormGroupTouched(form: FormGroup): void {
    Object.values(form.controls).forEach((control) => {
      control.markAsTouched();
      if ((control as any).controls) this.markFormGroupTouched(control as FormGroup);
    });
  }

  translate(key: string): string {
    return this.languageService.translate(key);
  }

  filterTimezones(
    form: FormGroup,
    allTimezones: WizardTimezone[],
    query: string
  ): WizardTimezone[] {
    const normalized = query.trim().toLowerCase();
    const filtered = normalized
      ? allTimezones.filter((item) =>
        item.value.toLowerCase().includes(normalized)
          || item.label.toLowerCase().includes(normalized)
      )
      : [...allTimezones];
    this.selectOnlyTimezone(form, filtered);
    return filtered;
  }

  selectOnlyTimezone(form: FormGroup, timezones: WizardTimezone[]): void {
    if (timezones.length === 1) form.patchValue({ timezone: timezones[0].value });
  }

  refreshCountries(form: FormGroup, language: Language): LocalizedCountry[] {
    const countries = this.utilityService.getLocalizedCountries(language);
    const current = form?.get('country')?.value;
    if (typeof current !== 'string' || !current.trim()) return countries;

    const localized = this.getLocalizedCountryName(current, language);
    if (localized !== current) form.patchValue({ country: localized });
    return countries;
  }

  getLocalizedCountryName(country: unknown, language: Language): string {
    if (typeof country !== 'string' || !country.trim()) return '';
    return this.utilityService.getLocalizedCountryName(country, language) || country.trim();
  }

  private hasValidProfile(form: FormGroup): boolean {
    const controls = [
      'firstName', 'secondName', 'country', 'jobTitle', 'company',
      'organizationType', 'organizationSize', 'timezone',
    ];
    return controls.every((control) => form.get(control)?.valid);
  }
}
